import React from 'react'
import tw from 'twin.macro'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { RevealableNumber } from 'src/components/payments-ops/bank-onboarding/RevealableNumber'
import { StatusChip } from 'src/components/payments-ops/StatusChip'
import { RowActionsMenu } from 'src/components/shared/grid/RowActionsMenu'
import { BankOnboardingStatus } from 'src/constants/payments-ops/enums'
import { bankOnboardingStatusLabels } from 'src/constants/payments-ops/labels'
import type { BankOnboarding } from 'src/interfaces/bankOnboarding'
import { formatDateTime, parseUnixTimestamp } from 'src/utils/grid'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'

const START_NEW_DISABLED_REASON =
  'An onboarding is already in progress or completed for this partner.'

export const buildBankOnboardingColumns = (
  // null without `bank_onboarding_manage`: the only row action is hidden, so the column is dropped.
  onStartNewFrom: ((onboarding: BankOnboarding) => void) | null,
): Array<GridColDef<BankOnboarding>> => {
  const columns: Array<GridColDef<BankOnboarding>> = [
    { field: 'partnerName', headerName: 'Partner', flex: 1, minWidth: 180 },
    {
      field: 'status',
      headerName: 'Status',
      width: 220,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BankOnboarding, BankOnboardingStatus>) => {
        const status = params.value ? bankOnboardingStatusLabels[params.value] : undefined

        if (!status) {
          return <span>{params.value ?? '—'}</span>
        }

        return <StatusChip label={status.label} tone={status.tone} />
      },
    },
    {
      field: 'balance',
      headerName: 'Balance',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number | undefined) => {
        return formatCentsAsUsd(value)
      },
    },
    {
      field: 'onboardingId',
      headerName: 'US Bank onboarding ID',
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<BankOnboarding, string | null>) => {
        return <span css={tw`truncate font-mono text-xs`}>{params.value ?? '—'}</span>
      },
    },
    {
      field: 'routingNumber',
      headerName: 'Routing',
      width: 170,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BankOnboarding, string | undefined>) => {
        return <RevealableNumber value={params.value} label="routing number" />
      },
    },
    {
      field: 'accountNumber',
      headerName: 'Account',
      width: 190,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BankOnboarding, string | undefined>) => {
        return <RevealableNumber value={params.value} label="account number" />
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created at',
      width: 180,
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateTime,
    },
  ]

  if (onStartNewFrom === null) {
    return columns
  }

  columns.push({
    field: 'actions',
    headerName: '',
    width: 70,
    sortable: false,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<BankOnboarding>) => {
      const isClosed = params.row.status === BankOnboardingStatus.ApplicationClosed

      return (
        <RowActionsMenu
          ariaLabel="Onboarding actions"
          dataTestId={`bank-onboarding-actions-${params.row.id}`}
          items={[
            {
              label: 'Start new from this',
              disabled: !isClosed || !params.row.payload,
              disabledReason: isClosed ? 'No stored details to copy.' : START_NEW_DISABLED_REASON,
              onClick: () => {
                onStartNewFrom(params.row)
              },
            },
          ]}
        />
      )
    },
  })

  return columns
}
